// tslint:disable
// @ts-ignore

import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, makeStyles } from "@material-ui/core";
import { Close as CloseIcon, Search as SearchIcon } from "@material-ui/icons";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ContainedButton, DefaultButton, Suggestion, ButtonGroup } from "src/common/components";
import { IParticipant, IParticipantsResponse } from "src/common/types";
import { postCall } from "src/services";

const useStyles = makeStyles((theme: any) => ({
  actionsContainer: {
    padding: "16px 24px"
  },
  closeButton: {
    color: theme.palette.grey[500],
    position: "absolute" as "absolute",
    right: 8,
    top: 8
  },
  content: {
    maxWidth: "100%",
    width: 578
  },
  searchIcon: {
    fill: "#bdbdbd",
    fontSize: "1.75rem",
    left: 19,
    position: "absolute" as "absolute",
    top: 12
  },
  searchInput: {
    "&:focus": {
      border: `1px solid ${theme.palette.primary.main}`
    },
    border: "1px solid #e6e6e6",
    display: "block" as "block",
    fontSize: "1.8rem",
    lineHeight: "41px",
    margin: -1,
    padding: "0 12px 0 48px",
    width: "calc(100% + 2px)"
  },
  selectedWrapper: {
    position: "relative" as "relative"
  },
  submitBtn: {
    minWidth: "auto" as "auto"
  },
  suggestionsWrapper: {
    border: "1px solid #e6e6e6",
    boxShadow: " 0 2px 4px 0 rgba(219, 219, 219, 0.5)",
    display: "block" as "block",
    maxWidth: "100%",
    position: "relative" as "relative",
    width: 530
  },
  buttonGroup: {
    display: "flex",
    marginRight: 40
  }
}));

interface IProps {
  onReassign: (data: { supervisorId: string; temporary: boolean; participant: any }) => void;
  onClose: () => void;
  open: boolean;
  talkToReassign: string;
  participant: any;
}

const ReassignTalkModal: React.FC<IProps> = ({ open: modalOpen, onClose, onReassign, participant }) => {
  const classes = useStyles();

  const [suggestions, setSuggestions] = React.useState<IParticipant[]>([]);
  const [searchText, updateSearchText] = React.useState("");
  const [temporary, updateTemporary] = React.useState(true);
  const [currentSelection, updateCurrentSelection] = React.useState<IParticipant | null>(null);

  const {
    t,
    i18n: { language: lng }
  } = useTranslation();

  const handleReassign = React.useCallback(() => {
    if (currentSelection) {
      onReassign({
        supervisorId: currentSelection.id,
        temporary,
        participant
      });
    }
    onClose();
  }, [currentSelection, temporary]);

  const clearSelection = React.useCallback(() => {
    updateCurrentSelection(null);
  }, []);

  const buttons = React.useMemo(() => {
    return [
      {
        label: t("feedback.page.my-employees.reassignSupervisor.options.temporary"),
        value: true,
        dataTestId: "temporary-btn"
      },
      {
        label: t("feedback.page.my-employees.reassignSupervisor.options.permanent"),
        value: false,
        dataTestId: "permanent-btn"
      }
    ];
  }, [lng]);

  const handleSearchChange = React.useCallback(({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    updateSearchText(value);
  }, []);

  const handleSelection = React.useCallback(data => {
    updateCurrentSelection(data);
  }, []);

  React.useEffect(() => {
    const searchTerm = searchText.trim();

    if (searchTerm.length > 2) {
      postCall({
        url: "/v1/feedback/participant/search",
        body: {
          page: 0,
          pageSize: 10,
          searchTerm
        }
      }).then(({ entities }: IParticipantsResponse) => {
        setSuggestions(entities.filter(result => participant.id !== result.id).slice(0, 3));
      });
    } else {
      setSuggestions([]);
    }
  }, [searchText]);

  return (
    <Dialog onClose={onClose} open={modalOpen}>
      <DialogTitle>
        <Grid alignItems="center" container spacing={2}>
          <Grid xs={12} md={6} item>
            {t(`feedback.page.my-employees.reassignSupervisor.title`)}
          </Grid>
          <Grid xs={12} md={6} item>
            <ButtonGroup
              value={temporary}
              buttons={buttons}
              onChange={updateTemporary}
              customStyle={classes.buttonGroup}
            />
          </Grid>
        </Grid>
      </DialogTitle>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
      <DialogContent className={classes.content}>
        {currentSelection ? (
          <div className={classes.selectedWrapper}>
            <IconButton className={classes.closeButton} aria-label="clear selection" onClick={clearSelection}>
              <CloseIcon />
            </IconButton>
            <Suggestion participant={currentSelection} />
          </div>
        ) : (
          <div data-qa-selector="peerSearch" className={classes.suggestionsWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <input
              autoFocus
              className={classes.searchInput}
              value={searchText}
              onChange={handleSearchChange}
              type="text"
              data-qa-selector="peerSearchInput"
            />

            {suggestions.map(participant => {
              const { id } = participant;
              return <Suggestion onSelection={handleSelection} key={id} participant={participant} />;
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <DefaultButton className={classes.submitBtn} onClick={onClose} dataTestId={`cancel-talk-button`}>
          {t(`feedback.page.my-employees.reassignSupervisor.cancel`)}
        </DefaultButton>
        <ContainedButton
          onClick={handleReassign}
          className={classes.submitBtn}
          disabled={!currentSelection}
          size="small"
          dataTestId={`reassign-talk-button`}
        >
          {t(`feedback.page.my-employees.reassignSupervisor.assign`)}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ReassignTalkModal);
